import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import { Link } from 'react-router-dom';
import { FormattedDate, injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';

import AgeRatings from 'Components/Product/Age/AgeRatings';
import SupportedLanguages from 'Components/Product/ProductAboutSection/SupportedLanguages';
import SteamDeckInformation from 'Components/Utility/SteamDeck/SteamDeckInformation';
import ProductUserTags from 'Components/Product/UserTags/ProductUserTags';
import Tooltip from 'Components/Molecules/Tooltip';
import FantasyVerseStripBanner from 'Components/FantasyVerse/FantasyVerseStripBanner';

import 'Components/Product/ProductAboutSection/ProductDetails.scss';

const UtalkContainer = lazy(() => import(/* webpackChunkName: "UtalkContainer" */ 'Components/Product/ProductAboutSection/UtalkContainer'));

export const titleMessages = defineMessages({
  publishers: {
    id: 'new.productdetails.publishers',
    defaultMessage: '{count, plural, one {Publisher} other {Publishers}}',
  },
  systems: {
    id: 'new.productdetails.systems',
    defaultMessage: '{count, plural, one {Operating System} other {Operating Systems}}',
  },
  platforms: {
    id: 'new.productdetails.platforms',
    defaultMessage: '{count, plural, one {Platform} other {Platforms}}',
  },
  combos: {
    id: 'new.productdetails.combos',
    defaultMessage: '{count, plural, one {Collection} other {Collections}}',
  },
  themes: {
    id: 'new.productdetails.themes',
    defaultMessage: '{count, plural, one {Theme} other {Themes}}',
  },
  playstyles: {
    id: 'new.productdetails.playstyles',
    defaultMessage: '{count, plural, one {Play Style} other {Play Styles}}',
  },
  developers: {
    id: 'new.productdetails.developers',
    defaultMessage: '{count, plural, one {Developer} other {Developers}}',
  },
  released: {
    id: 'new.productdetails.released',
    defaultMessage: 'Release Date',
  },
  orderDate: {
    id: 'new.productdetails.orderDate',
    defaultMessage: 'Order Date',
  },
  downloadDate: {
    id: 'new.productdetails.downloadDate',
    defaultMessage: 'Preload Date',
  },
  genres: {
    id: 'new.productdetails.genres',
    defaultMessage: '{count, plural, one {Genre} other {Genres}}',
  },
  features: {
    id: 'new.productdetails.features',
    defaultMessage: '{count, plural, one {Feature} other {Features}}',
  },
  franchises: {
    id: 'new.productdetails.franchise',
    defaultMessage: '{count, plural, one {Franchise} other {Franchises}}',
  },
  link: {
    id: 'new.productdetails.Link',
    defaultMessage: 'Link',
  },
  rating: {
    id: 'new.productdetails.rating',
    defaultMessage: 'Rating',
  },
  supported_languages: {
    id: 'new.productdetails.supportedlanguages',
    defaultMessage: 'Supported Languages',
  },
});

// Had to create a new capitlize method because the lodash one has changed from amen
// use lodash CamelCase
const capitalize = tag => tag.charAt(0).toUpperCase() + tag.slice(1);

const listElement = (title, func, label) =>
  <dl key={title || label} className="product-details">
    <dt>{label || capitalize(title)}:</dt>
    <dd>{func()}</dd>
  </dl>;

const validLink = (link) => {
  let url = link;

  if (!link.match('^(http|https)://.*$')) {
    url = `http://${link}`;
  }
  return url;
};

const searchLink = (key, slug, label) => <Link to={`/${key}/${slug}`}>{label || slug}</Link>;

// TODO Refactor - pass in whole product, stop repeated blocks of code

function ProductDetails({
  data,
  released,
  links,
  noReleaseDate,
  orderDate,
  intl,
  nintendoProduct,
  currentDrm,
  giveaway,
  vrSupport,
  playableFrom,
  earlyAccess,
}) {
  const { formatMessage } = intl;

  const {
    features,
    genres,
    themes,
    playstyles,
    combo,
    publishers,
    developers,
    platforms,
    franchises,
    slug,
    age,
    languages,
    display_type: displayType,
    type,
    name: pName,
    userTags,
    parent_slug: parentSlug,
    xboxPlatforms,
    collections,
  } = data;

  const product = {
    slug,
    type,
    name: pName,
    userTags,
    parent_slug: parentSlug,
  };

  const { url } = links;

  const releaseDay = new Date(released).getDate();
  const releaseMonth = new Date(released).getMonth();

  let hideDate = false;
  if (releaseDay === 31 && releaseMonth === 11 && Date.parse(released) > Date.now()) {
    hideDate = true;
  }

  let showRatings = false;
  const ratings = Object.keys((age || {}));

  ratings.forEach((key) => {
    if (age[key] > 0) showRatings = true;
  });

  let bitdefender = false;
  if (publishers && publishers.length === 1 && publishers[0].name && publishers[0].name.toLowerCase() === 'bitdefender') {
    bitdefender = true;
  }
  if (publishers && publishers.length === 1 && publishers[0].toLowerCase && publishers[0].toLowerCase() === 'bitdefender') {
    bitdefender = true;
  }

  const formatPlatform = (platform) => {
    if (platform === 'epicgames') return 'Epic Games';
    if (platform === 'uplay') return 'Ubisoft Connect';
    return platform;
  };

  const checkOculusPlatform = (vr) => {
    if (
      vr.oculus_rift ||
      vr.oculus_meta_quest ||
      vr.oculus_meta_quest_two ||
      vr.oculus_meta_quest_three
    ) {
      return true;
    }
    return false;
  };

  /* eslint-disable max-len */
  const formatOculusPlatform = vr =>
    <React.Fragment>
      {vr.oculus_rift &&
        <span className="text-capitalize">
          Oculus Rift
        </span>
      }
      {vr.oculus_rift && (vr.oculus_meta_quest || vr.oculus_meta_quest_two || vr.oculus_meta_quest_three) && <br />}
      {vr.oculus_meta_quest &&
        <span className="text-capitalize">
          Meta Quest
        </span>
      }
      {vr.oculus_meta_quest && (vr.oculus_meta_quest_two || vr.oculus_meta_quest_three) && <br />}
      {vr.oculus_meta_quest_two &&
        <span className="text-capitalize">
          Meta Quest 2
        </span>
      }
      {vr.oculus_meta_quest && vr.oculus_meta_quest_two && <br />}
      {vr.oculus_meta_quest_three &&
        <span className="text-capitalize">
          Meta Quest 3
        </span>
      }
    </React.Fragment>;

  /* eslint-disable max-len */
  const formatXboxPlatform = platform =>
    <React.Fragment>
      {platform.xbox_series_x_s &&
        <span className="text-capitalize">
          Xbox Series X|S
        </span>
      }
      {platform.xbox_series_x_s && (platform.xbox_one || platform.windows_10_11) && <br />}
      {platform.xbox_one &&
        <span className="text-capitalize">
          Xbox One
        </span>
      }
      {platform.xbox_one && platform.windows_10_11 && <br />}
      {platform.windows_10_11 &&
        <span className="text-capitalize">
          Windows 10
        </span>
      }
    </React.Fragment>;

  let showXboxPlatforms = false;
  if (
    !isEmpty(xboxPlatforms) &&
    (xboxPlatforms.xbox_series_x_s || xboxPlatforms.xbox_one || xboxPlatforms.windows_10_11)) {
    showXboxPlatforms = true;
  }

  const isPartOfFantasyverse = includes(collections, 'FantasyVerse');

  let bethesdaProduct = false;
  publishers.forEach((pub) => {
    if (pub.name && includes(pub.name.toLowerCase(), 'bethesda')) bethesdaProduct = true;
  });

  return (
    <div className="product-details-container">
      {currentDrm !== 'utalk' &&
        <React.Fragment>
          {isPartOfFantasyverse &&
            <FantasyVerseStripBanner />
          }

          <h3 className="section-heading">
            {displayType !== 'software' && displayType !== 'dlc' && displayType !== 'book' && displayType !== 'audio' &&
              <FormattedMessage id="new.productdetails.title" defaultMessage="Game Details" tagName="span" />
            }
            {displayType === 'software' &&
              <FormattedMessage id="new.productdetails.title.product" defaultMessage="Product Details" tagName="span" />
            }
            {displayType === 'dlc' &&
              <FormattedMessage id="new.productdetails.title.dlc" defaultMessage="DLC Details" tagName="span" />
            }
            {displayType === 'book' &&
              <FormattedMessage id="new.productdetails.title.book" defaultMessage="eBook Details" tagName="span" />
            }
            {displayType === 'audio' &&
              <FormattedMessage id="new.productdetails.title.audio" defaultMessage="Audio Details" tagName="span" />
            }
          </h3>

          {bethesdaProduct &&
            <div className="licence-message">
              <FormattedMessage
                id="productdetails.bethesda-licence"
                defaultMessage="Buying this digital item is a license. By selecting Buy, you agree to the {link}, including the applicable EULA."
                values={{
                  link: <a href="https://documents.bethesda.net/en/terms-of-service" target="_blank" rel="noopener noreferrer">
                    <FormattedMessage id="productdetails.bethesda-licence.link" defaultMessage="Terms of Service" />
                  </a>,
                }}
              />
            </div>
          }

          <div className="product-details-content">
            <div className="product-details-left">
              {!isEmpty(platforms) && platforms[0] !== 'drm_free' &&
                listElement('platforms', () => platforms.map((platform, i) => {
                  if (platform === 'oculus' && checkOculusPlatform(vrSupport)) {
                    return formatOculusPlatform(vrSupport);
                  }

                  return (
                    <span key={platform} className="text-capitalize">
                      {i > 0 && <br />}
                      {/* eslint-disable no-nested-ternary */}
                      {
                        formatPlatform(platform)
                      }
                    </span>
                  );
                },
                ), formatMessage(titleMessages.platforms, { count: platforms.length }),
                )
              }

              {showXboxPlatforms &&
                <dl className="product-details">
                  <dt>
                    <FormattedMessage id="new.productdetails.title.xbox-platforms" defaultMessage="Playable On" />:
                  </dt>
                  <dd>
                    {formatXboxPlatform(xboxPlatforms)}
                  </dd>
                </dl>
              }

              {!isEmpty(released) && !noReleaseDate &&
                listElement('released', () =>
                  <div>
                    {hideDate &&
                      <FormattedDate value={released} year="numeric" />
                    }
                    {!hideDate && !bitdefender &&
                      <FormattedDate value={released} year="numeric" month="long" day="2-digit" />
                    }
                    {!hideDate && bitdefender &&
                      <div>
                        <FormattedMessage id="new.productdetails.release_date.bitdefender_version" defaultMessage="Latest version always supplied" />
                      </div>
                    }
                  </div>, formatMessage(titleMessages.released),
                )
              }

              {orderDate && new Date().toISOString() < orderDate &&
                listElement('preorder', () =>
                  <div>
                    <FormattedDate value={orderDate} year="numeric" month="long" day="2-digit" />
                  </div>, formatMessage(titleMessages.orderDate),
                )
              }

              {earlyAccess && Date.now() < Date.parse(playableFrom) &&
                <dl className="product-details">
                  <dt><FormattedMessage id="new.productdetails.row-title.early-access" defaultMessage="Early Access Date:" /></dt>
                  <dd>
                    <Tooltip
                      ariaLabel="Early Access dropdown. Expand for more details"
                      content={
                        <FormattedMessage id="new.productdetails.row-title.early-access.tooltip" defaultMessage="No need to wait until release day, you can start playing your game from this date." />
                      }
                    >
                      <FormattedDate value={playableFrom} year="numeric" month="long" day="2-digit" />
                      <FontAwesomeIcon icon={faCircleInfo} />
                    </Tooltip>
                  </dd>
                </dl>
              }

              {!isEmpty(developers) &&
                listElement('developers', () => developers.map((d, i) => {
                  const developer = d;
                  return (
                    <span key={developer}>
                      {i > 0 && <br />}{developer}
                    </span>
                  );
                }), formatMessage(titleMessages.developers, { count: developers.length }),
                )
              }

              {!isEmpty(publishers) &&
                listElement('publishers', () =>
                  publishers.map((publisher, i) => {
                    const { name, link, slug: facetSlug } = typeof publisher === 'string' ? { name: publisher, link: false } : publisher;
                    return (
                      <span key={name}>
                        {i > 0 && <br />}
                        {link ? searchLink('publishers', facetSlug, name) : name}
                      </span>
                    );
                  }), formatMessage(titleMessages.publishers, { count: publishers.length }),
                )
              }

              {showRatings &&
                listElement('rating', () =>
                  <div>
                    <AgeRatings
                      ratings={age}
                      nintendoProduct={nintendoProduct}
                      slug={slug}
                    />
                  </div>, formatMessage(titleMessages.rating),
                )
              }

              {!isEmpty(languages) &&
                listElement('supportedLanguages', () =>
                  <div>
                    <SupportedLanguages languages={languages} />
                  </div>, formatMessage(titleMessages.supported_languages),
                )
              }

              {data.steam && data.steam.deck_support &&
                <div>
                  <dl className="row product-details">
                    <dt><Link to="/steam-deck-games"><FormattedMessage id="new.productdetails.steam_deck_support" defaultMessage="Steam Deck Support" /></Link>:</dt>
                    {data.steam.deck_support === 'verified' &&
                      <dd>
                        <SteamDeckInformation
                          type={data.steam.deck_support}
                          details={data.steam.deck_details}
                          note={data.steam.deck_note}
                          product={data.name}
                        />
                      </dd>
                    }
                    {data.steam.deck_support === 'playable' &&
                      <dd>
                        <SteamDeckInformation
                          type={data.steam.deck_support}
                          details={data.steam.deck_details}
                          note={data.steam.deck_note}
                          product={data.name}
                        />
                      </dd>
                    }
                    {data.steam.deck_support === 'unsupported' &&
                      <dd><FormattedMessage id="new.productdetails.steam_deck_support.unsupported" defaultMessage="Unsupported" /></dd>
                    }
                    {data.steam.deck_support === 'unknown' &&
                      <dd><FormattedMessage id="new.productdetails.steam_deck_support.unknown" defaultMessage="Unknown" /></dd>
                    }
                  </dl>
                </div>
              }

              {!isEmpty(franchises) &&
                listElement('franchises', () =>
                  franchises.map((franchise, i) => {
                    const { name, link, slug: facetSlug } = typeof franchise === 'string' ? { name: franchise, link: false } : franchise;
                    return (
                      <span key={name}>
                        {i > 0 && <br />}
                        {link ? searchLink('franchises', facetSlug, name) : name}
                      </span>
                    );
                  }), formatMessage(titleMessages.franchises, { count: franchises.length }),
                )
              }

              {links && (links.url) && listElement('link', () =>
                <div>
                  {url &&
                    <div>
                      <a
                        href={validLink(links.url)}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                      >
                        <FormattedMessage id="new.productdetails.website" defaultMessage="View the website" tagName="span" />
                        <FontAwesomeIcon icon={faUpRightFromSquare} className="ml-1" />
                      </a>
                    </div>
                  }
                </div>, formatMessage(titleMessages.link),
              )}
            </div>

            <div className="product-details-right">
              <ProductUserTags product={product} />

              {!isEmpty(genres) &&
                listElement('genres', () =>
                  genres.map((genre, i) => {
                    const { name, link, slug: facetSlug } = typeof genre === 'string' ? { name: genre, link: false } : genre;
                    return (
                      <span key={name}>
                        {i > 0 && <br />}
                        {link ? searchLink('categories', facetSlug, name) : name}
                      </span>
                    );
                  }), formatMessage(titleMessages.genres, { count: genres.length }),
                )
              }

              {!isEmpty(themes) &&
                listElement('themes', () =>
                  themes.map((theme, i) => {
                    const { name, link, slug: facetSlug } = typeof theme === 'string' ? { name: theme, link: false } : theme;
                    return (
                      <span key={name}>
                        {i > 0 && <br />}
                        {link ? searchLink('categories', facetSlug, name) : name}
                      </span>
                    );
                  }), formatMessage(titleMessages.themes, { count: themes.length }),
                )
              }

              {!isEmpty(playstyles) &&
                listElement('playstyles', () =>
                  playstyles.map((playstyle, i) => {
                    const { name, link, slug: facetSlug } = typeof playstyle === 'string' ? { name: playstyle, link: false } : playstyle;
                    return (
                      <span key={name}>
                        {i > 0 && <br />}
                        {link ? searchLink('categories', facetSlug, name) : name}
                      </span>
                    );
                  }), formatMessage(titleMessages.playstyles, { count: playstyles.length }),
                )
              }

              {!isEmpty(combo) &&
                listElement('combo', () =>
                  combo.map((playstyle, i) => {
                    const { name, link, slug: facetSlug } = typeof playstyle === 'string' ? { name: playstyle, link: false } : playstyle;
                    return (
                      <span key={name}>
                        {i > 0 && <br />}
                        {link ? searchLink('categories', facetSlug, name) : name}
                      </span>
                    );
                  }), formatMessage(titleMessages.combos, { count: combo.length }),
                )
              }

              {!isEmpty(features) &&
                listElement('features', () =>
                  features.map((feature, i) => {
                    const { name } = typeof feature === 'string' ? { name: feature } : feature;
                    return (
                      <span key={name}>
                        {i > 0 && <br />}
                        {name}
                      </span>
                    );
                  }), formatMessage(titleMessages.features, { count: features.length }),
                )
              }
            </div>
          </div>
        </React.Fragment>
      }

      {currentDrm === 'utalk' &&
        <Suspense fallback={<div />}>
          <UtalkContainer giveaway={giveaway} />
        </Suspense>
      }
    </div>
  );
}

ProductDetails.propTypes = {
  data: PropTypes.shape({
    drm: PropTypes.arrayOf(PropTypes.string),
    systems: PropTypes.arrayOf(PropTypes.string),
    userTags: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  released: PropTypes.string,
  playableFrom: PropTypes.string,
  earlyAccess: PropTypes.bool,
  orderDate: PropTypes.string,
  noReleaseDate: PropTypes.bool,
  links: PropTypes.shape({
    url: PropTypes.string,
    steam: PropTypes.shape({
      id: PropTypes.number,
    }),
  }),
  nintendoProduct: PropTypes.bool,
  currentDrm: PropTypes.string,
  giveaway: PropTypes.bool,
  vrSupport: PropTypes.shape({}),
};

ProductDetails.defaultProps = {
  data: {
    drm: [],
    systems: [],
    userTags: [],
  },
  released: '',
  playableFrom: '',
  earlyAccess: false,
  orderDate: '',
  noReleaseDate: false,
  links: {
    steam: {},
  },
  nintendoProduct: false,
  currentDrm: '',
  giveaway: false,
  vrSupport: {},
};

export default injectIntl(ProductDetails);
